import { MagnifyingGlassIcon, Squares2X2Icon, TableCellsIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { CollapsibleSection } from './CollapsibleSection'
import type { ApplicationStatus } from '../../types'

type SortKey = 'company' | 'status' | 'salary' | 'stages' | 'createdAt' | 'updatedAt'
type SortDir = 'asc' | 'desc'

type Props = {
  viewMode: 'active' | 'archived'
  onViewModeChange: (mode: 'active' | 'archived') => void
  companyFilter: string
  onCompanyFilterChange: (value: string) => void
  statusFilter: 'all' | ApplicationStatus
  onStatusFilterChange: (value: 'all' | ApplicationStatus) => void
  availableStatusFilters: ApplicationStatus[]
  statusLabels: Record<ApplicationStatus, string>
  displayMode: 'cards' | 'table'
  onDisplayModeChange: (mode: 'cards' | 'table') => void
  sortKey: SortKey
  sortDir: SortDir
  onSortChange: (key: SortKey, dir: SortDir) => void
  collapsible?: boolean
}

export const DashboardFiltersToolbar = ({
  viewMode,
  onViewModeChange,
  companyFilter,
  onCompanyFilterChange,
  statusFilter,
  onStatusFilterChange,
  availableStatusFilters,
  statusLabels,
  displayMode,
  onDisplayModeChange,
  sortKey,
  sortDir,
  onSortChange,
  collapsible = false,
}: Props) => {
  const content = (
    <div className="rounded-lg border border-app-line bg-app-subtle px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Active/Archived toggle */}
        <div className="flex items-center rounded-lg border border-app-line p-0.5 flex-shrink-0">
          <button
            onClick={() => onViewModeChange('active')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              viewMode === 'active'
                ? 'bg-app-highlight text-app-accent'
                : 'text-app-muted hover:text-app-ink'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => onViewModeChange('archived')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              viewMode === 'archived'
                ? 'bg-app-highlight text-app-accent'
                : 'text-app-muted hover:text-app-ink'
            }`}
          >
            Archived
          </button>
        </div>

        <div className="h-6 w-px bg-app-line hidden sm:block flex-shrink-0" />

        {/* Search */}
        <div className="relative w-40 flex-shrink-0">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted"
            aria-hidden="true"
          />
          <input
            type="text"
            value={companyFilter}
            onChange={(e) => onCompanyFilterChange(e.target.value)}
            placeholder="Filter by company"
            className="w-full rounded-lg border border-app-line bg-app-surface py-1.5 pl-9 pr-3 text-sm text-app-ink focus:border-app-accent focus:outline-none"
          />
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as typeof statusFilter)}
          className="flex-shrink-0 rounded-lg border border-app-line bg-app-surface px-2 py-1.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
        >
          <option value="all">All statuses</option>
          {availableStatusFilters.map((status) => (
            <option key={status} value={status}>
              {statusLabels[status]}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={`${sortKey}_${sortDir}`}
          onChange={(e) => {
            const [key, dir] = e.target.value.split('_') as [SortKey, SortDir]
            onSortChange(key, dir)
          }}
          className="flex-shrink-0 rounded-lg border border-app-line bg-app-surface px-2 py-1.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
        >
          <option value="createdAt_desc">Newest first</option>
          <option value="createdAt_asc">Oldest first</option>
          <option value="updatedAt_desc">Recently updated</option>
          <option value="updatedAt_asc">Least recently updated</option>
          <option value="company_asc">Company A→Z</option>
          <option value="company_desc">Company Z→A</option>
          <option value="status_asc">Status A→Z</option>
          <option value="status_desc">Status Z→A</option>
          <option value="salary_desc">Highest salary</option>
          <option value="salary_asc">Lowest salary</option>
          <option value="stages_desc">Most progress</option>
          <option value="stages_asc">Least progress</option>
        </select>

        {/* View toggle - pushed to end */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => onDisplayModeChange('table')}
            title="Table view"
            aria-label="Table view"
            className={`inline-flex items-center rounded-lg p-1.5 text-sm font-semibold ${
              displayMode === 'table'
                ? 'bg-app-highlight text-app-accent'
                : 'border border-app-line text-app-ink hover:bg-app-subtle'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDisplayModeChange('cards')}
            title="Cards view"
            aria-label="Cards view"
            className={`inline-flex items-center rounded-lg p-1.5 text-sm font-semibold ${
              displayMode === 'cards'
                ? 'bg-app-highlight text-app-accent'
                : 'border border-app-line text-app-ink hover:bg-app-subtle'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )

  if (collapsible) {
    return (
      <CollapsibleSection title="Filters & View" icon={FunnelIcon} storageKey="dashboard-filters-expanded" defaultExpanded={false} mobileOnly>
        {content}
      </CollapsibleSection>
    )
  }

  return content
}
